import { motion } from "framer-motion";
import { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import Footer from "@/components/Footer";

// Photo references from centralized catalog
const images = {
  hero1: PHOTOS.FEMALE_ONLY.threeWomenDanceFloor,
  hero2: PHOTOS.DANCING.crowdCheeringCaptainHat,
};

// FAQ Data - Yacht Rock Detroit 2026
const faqCategories = [
  {
    id: "general",
    title: "General",
    questions: [
      { q: "Where is Yacht Rock Detroit taking place?", a: "The event takes place at The Roostertail, located at 100 Marquette Dr, Detroit, MI 48214." },
      { q: "When is the event?", a: "Friday, July 17th and Saturday, July 18th, 2026." },
      { q: "Is this a 21+ event?", a: "Yes — Yacht Rock Detroit is strictly 21+." },
      { q: "What kind of music will there be?", a: "A curated lineup of live bands and DJs specializing in Yacht Rock, Classic Rock, and smooth hits from the late 70s and early 80s." },
      { q: "Will the event happen rain or shine?", a: "Yes, the festival is a rain or shine event." },
      { q: "Can I come by boat?", a: "Boat slips may be available at a later date — check back for updates. Boaters in the surrounding area may be able to hear the music, but access to the property requires a valid boat slip reservation." },
    ]
  },
  {
    id: "tickets",
    title: "Tickets & Passes",
    questions: [
      { q: "Where can I buy tickets?", a: "Tickets are available exclusively through our official website, yachtrockdetroit.com." },
      { q: "What is the Admiral's Pass?", a: "The Admiral's Pass is our all-inclusive VIP experience, featuring food and a dedicated bar. These are very limited — grab yours before they're gone." },
      { q: "Are there VIP options?", a: "Yes — the Admiral's Pass is our premium all-inclusive option with food and a dedicated bar. Availability is very limited." },
      { q: "Will there be DJs on the deck?", a: "Yes! DJs will be spinning on the deck throughout the festival." },
      { q: "Can I get a refund if I can't make it?", a: "All ticket sales are final. No refunds or exchanges will be provided." },
      { q: "Do I need to print my ticket?", a: "No, you can show your digital ticket on your mobile device at the gate for scanning." },
    ]
  },
  {
    id: "parking",
    title: "Parking",
    questions: [
      { q: "Is there parking available?", a: "Parking details will be updated at a later date. We recommend checking back closer to the event for the latest information. Rideshare services like Uber or Lyft are always a great option." },
    ]
  },
  {
    id: "experience",
    title: "Event Experience",
    questions: [
      { q: "Is there a dress code?", a: "Nautical attire, captain's hats, and leisure wear are highly encouraged! Think boat shoes, linen, and floral prints." },
      { q: "What time do gates open?", a: "Specific gate times vary by ticket type. Please check your ticket confirmation or our Schedule page for exact timing." },
      { q: "Are coolers allowed?", a: "No. For the safety and enjoyment of all guests, coolers of any kind (including soft-sided) are not permitted inside the festival grounds." },
      { q: "Can I bring a chair?", a: "No chairs of any kind are permitted inside the festival grounds." },
      { q: "Can I bring my pet?", a: "No pets are allowed inside the festival grounds, with the exception of documented service animals." },
    ]
  },
  {
    id: "food",
    title: "Food & Drink",
    questions: [
      { q: "Will there be food available?", a: "Yes, food will be available on-site. Vendor details will be updated at a later date." },
      { q: "Will there be alcohol?", a: "Yes, full bars will be serving themed cocktails, beer, wine, and non-alcoholic beverages." },
      { q: "Can I bring my own food or water?", a: "Outside food and beverages are not permitted inside the venue. One factory-sealed bottle of water (up to 20oz) is allowed per person." },
    ]
  },
  {
    id: "other",
    title: "Other",
    questions: [
      { q: "How can I become a vendor or sponsor?", a: "Please visit our Contact page and fill out the inquiry form to get in touch with our partnership team." },
    ]
  },
];

const FAQ = () => {
  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(cat =>
      cat.questions.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      }))
    ),
  }), []);

  usePageSEO({
    title: "FAQ — Yacht Rock Detroit 2026",
    description: "Answers to common questions about tickets, parking, food, dress code, and everything else for Yacht Rock Detroit.",
    keywords: "yacht rock detroit faq, festival questions, parking roostertail, tickets faq",
    ogImage: PHOTOS.DANCING.crowdCheeringCaptainHat,
    jsonLd: faqJsonLd,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "FAQ", url: "https://yachtrockdetroit.com/faq" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });
  
  const [activeCategory, setActiveCategory] = useState(faqCategories[0].id);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  // Update active category based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = faqCategories.map(cat => document.getElementById(cat.id));
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveCategory(faqCategories[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(categoryId);
    if (element) {
      const offset = 120; // Account for sticky nav
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hero Header Section - Blue with Noise */}
      <section 
        ref={headerRef}
        className="relative min-h-fit md:min-h-[70vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end"
      >
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Boats graphic background */}
        <img 
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{
            opacity: 0.15,
            filter: 'brightness(0) invert(1)',
          }}
        />

        {/* MOBILE LAYOUT */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Your Questions
          </motion.p>
          <div className="text-[7vw] font-bold text-white leading-[0.9] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Yacht Rock Detroit" duration={0.5} delayMultiple={0.04} />
            <br />
            <span className="text-[5vw]">
              <GradualSpacing text="Frequently Asked Questions" duration={0.5} delayMultiple={0.03} />
            </span>
          </div>
          
          {/* Images overlapping */}
          <div className="relative w-full max-w-xs h-[280px]">
            <motion.div
              className="absolute left-0 top-0 w-[55%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img src={images.hero1} alt="Three women dancing at Yacht Rock Detroit" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              className="absolute right-0 top-6 w-[55%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20"
              initial={{ opacity: 0, y: 30, rotate: 5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img src={images.hero2} alt="Crowd cheering with captain hats at the concert" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <motion.div
          className="hidden md:block absolute top-44 right-[8%] w-[28%] max-w-sm aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 50, rotate: 3 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 3 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={images.hero1} alt="Three women dancing at Yacht Rock Detroit" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div
          className="hidden md:block absolute bottom-20 right-[28%] w-[20%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10"
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <img src={images.hero2} alt="Crowd cheering with captain hats at the concert" className="w-full h-full object-cover" />
        </motion.div>

        {/* Big Title - Bottom Left (Desktop only) */}
        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-20">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Your Questions
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="FAQ" duration={0.5} delayMultiple={0.08} />
          </h1>
        </div>
      </section>

      {/* Yellow Accent Strip */}
      <section className="relative py-6 bg-yacht-yellow overflow-hidden">
        <div 
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 px-8 md:px-16">
          <p className="text-yacht-charcoal/80 text-lg md:text-xl font-medium text-center" style={{ fontFamily: 'Georgia, serif' }}>
            We've got answers.
          </p>
        </div>
      </section>

      {/* Sticky Category Pills */}
      <div className="sticky top-0 z-40 bg-yacht-cream/95 backdrop-blur-sm border-b border-yacht-charcoal/10">
        <div className="px-4 md:px-16 py-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 md:gap-3 justify-start md:justify-center min-w-max">
            {faqCategories.map((category, index) => (
              <motion.button
                key={category.id}
                onClick={() => scrollToCategory(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-yacht-blue text-white'
                    : 'bg-yacht-charcoal/10 text-yacht-charcoal hover:bg-yacht-charcoal/20'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                {category.title}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <section ref={contentRef} className="py-16 md:py-24 px-6 md:px-16">
        <div className="max-w-3xl mx-auto">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.id}
              id={category.id}
              className="mb-16 last:mb-0"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6 pb-2 border-b-2 border-yacht-blue/30">
                {category.title}
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((item, questionIndex) => (
                  <AccordionItem 
                    key={questionIndex} 
                    value={`${category.id}-${questionIndex}`}
                    className="relative rounded-2xl overflow-hidden border-0"
                  >
                    {/* White bg with noise */}
                    <div className="absolute inset-0 bg-white rounded-2xl" />
                    <div 
                      className="absolute inset-0 opacity-[0.08] pointer-events-none rounded-2xl"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                      }}
                    />
                    <AccordionTrigger className="relative z-10 px-6 md:px-8 py-5 md:py-6 text-left text-yacht-charcoal text-base md:text-lg font-semibold hover:no-underline [&[data-state=open]]:text-yacht-blue">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="relative z-10 px-6 md:px-8 pb-6 md:pb-8 pt-0 text-yacht-charcoal/70 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-yacht-charcoal">
        <div className="max-w-4xl mx-auto text-center px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Still have questions?
          </h2>
          <p className="text-white/70 text-lg mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            Drop us a line and we'll get back to you.
          </p>
          <a 
            href="mailto:hello@yachtrockdetroit.com"
            className="inline-block px-8 py-3.5 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg"
          >
            Contact Us
          </a>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FAQ;
